<template>
    <component
        :is="isMobile ? UiModalMobile : UiModal"
        :is-open="showModal"
        title=""
        :isOpen="showModal"
        @close="closeModal"
        width="1000px"
        modal-class="travel-plus-popup overflow-hidden"
    >
        <template #content>
            <div class="flex h-full flex-col-reverse sm:flex-row sm:h-[600px]">
                <!-- Left panel -->
                <div
                    class="flex flex-col px-8 sm:px-[50px] py-[30px] w-full sm:w-[530px] sm:shrink-0"
                >
                    <div class="pb-8">
                        <h3
                            class="text-[28px] sm:text-[38px] font-semibold leading-[36px] sm:leading-[48px] text-slate-800 dark:text-slate-100 mb-3"
                        >
                            Introducing
                            <br class="hidden sm:inline" />
                            <span class="text-orange">10xTravel+</span>
                        </h3>
                        <p class="text-sm text-slate-500 dark:text-slate-400 leading-5">
                            Unlock the full power of your rewards with automated
                            tools and a community of travel experts.
                        </p>
                    </div>

                    <ul class="flex flex-col space-y-4 pb-8">
                        <li
                            v-for="feature in features"
                            :key="feature.text"
                            class="flex items-center gap-2.5"
                        >
                            <Icon
                                :icon="feature.icon"
                                class="w-5 h-5 text-orange shrink-0"
                            />
                            <span class="text-sm text-slate-800 dark:text-slate-200">
                                {{ feature.text }}
                            </span>
                        </li>
                    </ul>

                    <div class="flex flex-col items-stretch gap-2 w-full">
                        <a
                            href="/10xtravel-plus"
                            class="inline-flex items-center justify-center w-full h-10 rounded bg-orange text-white text-sm font-medium hover:bg-orange-dark transition-colors"
                            @click="closeModal"
                        >
                            Learn more
                        </a>
                        <a
                            href="/10xtravel-plus/upgrade"
                            class="inline-flex items-center justify-center w-full h-10 rounded border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                            @click="closeModal"
                        >
                            Upgrade now
                        </a>
                    </div>
                </div>

                <!-- Right panel: award alerts slider -->
                <div
                    class="sm:flex-1 sm:bg-slate-50 dark:sm:bg-slate-700/50 flex flex-col overflow-hidden sm:h-auto"
                    :style="{
                        height: isMobile ? '320px' : undefined,
                        boxShadow: !isMobile
                            ? 'inset 2px 0 4px 0 rgba(0, 0, 0, 0.06)'
                            : undefined,
                    }"
                >
                    <div
                        class="flex-1 relative overflow-hidden sm:pt-[30px] px-4 sm:px-6"
                    >
                        <Carousel
                            v-model="currentSlide"
                            :items-to-show="1"
                            :wrap-around="true"
                            :autoplay="4000"
                            :transition="350"
                            :pause-autoplay-on-hover="true"
                            class="h-full"
                        >
                            <Slide v-for="slide in slides" :key="slide.image">
                                <img
                                    :src="slideImage(slide)"
                                    :alt="slide.caption"
                                    class="w-full h-full object-contain"
                                />
                            </Slide>
                        </Carousel>
                    </div>

                    <!-- Caption + navigation dots -->
                    <div class="shrink-0 sm:pb-[30px] mt-5 sm:mt-0">
                        <Transition name="fade" mode="out-in">
                            <p
                                :key="currentSlide"
                                class="text-sm text-center mb-1"
                            >
                                {{ currentSlideData.caption }}
                            </p>
                        </Transition>
                        <div class="flex items-center justify-center p-1">
                            <button
                                class="p-3 sm:p-2 rounded-[14px] hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                @click="prevSlide"
                            >
                                <i
                                    class="fa-light fa-chevron-left text-slate-400 text-base sm:text-xs"
                                ></i>
                            </button>
                            <div class="flex gap-3 sm:gap-1.5 mx-2 sm:mx-1">
                                <button
                                    v-for="(_, i) in slides"
                                    :key="i"
                                    class="w-2 h-2 sm:w-1 sm:h-1 rounded-full transition-colors"
                                    :class="
                                        i === currentSlide
                                            ? 'bg-slate-800 dark:bg-slate-100'
                                            : 'bg-slate-200 dark:bg-slate-600'
                                    "
                                    @click="currentSlide = i"
                                ></button>
                            </div>
                            <button
                                class="p-3 sm:p-2 rounded-[14px] hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                                @click="nextSlide"
                            >
                                <i
                                    class="fa-light fa-chevron-right text-slate-400 text-base sm:text-xs"
                                ></i>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </component>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/store/app';
import { ref, computed, watch, inject, onMounted, onUnmounted } from 'vue';
import { Carousel, Slide } from 'vue3-carousel';
import 'vue3-carousel/dist/carousel.css';
import UiModal from '@/components/ui/modal/ui-modal.vue';
import UiModalMobile from '@/components/ui/modal/ui-modal-mobile.vue';
import Icon from '@/components/ui/icon/icon.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

const $cookies = inject('$cookies');

const appStore = useAppStore();
const { activePopup } = storeToRefs(appStore);

const showModal = ref(false);
const currentSlide = ref(0);
const windowWidth = ref(window.innerWidth);

const isMobile = computed(() => windowWidth.value < 640);

const onResize = () => {
    windowWidth.value = window.innerWidth;
};
onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const slides = [
    {
        caption: 'Unlimited credit card benefits tracking',
        image: '/images/promo-slider/promo-slider_benefits_desktop.png',
        mobileImage:
            '/images/promo-slider/promo-slider-mobile_benefits_mobile.png',
    },
    {
        caption: 'Award alerts for the best deals',
        image: '/images/promo-slider/promo-slider_announce_award_alerts_desktop.png',
        mobileImage:
            '/images/promo-slider/promo-slider-mobile_award_alerts_mobile.png',
    },
    {
        caption: 'Members-only travel community',
        image: '/images/promo-slider/promo-slider_announce_community_desktop.png',
        mobileImage:
            '/images/promo-slider/promo-slider-mobile_community_mobile.png',
    },
    {
        caption: 'Maximize rewards for each spending category',
        image: '/images/promo-slider/promo-slider_announce_best_card_desktop.png',
        mobileImage:
            '/images/promo-slider/promo-slider-mobile_best_card_mobile.png',
    },
    {
        caption: 'Import redemptions from email',
        image: '/images/promo-slider/promo-slider_announce_redemptions_desktop.png',
        mobileImage:
            '/images/promo-slider/promo-slider-mobile_announce_redemptions_mobile.png',
    },
];

const slideImage = (slide) =>
    isMobile.value ? slide.mobileImage || slide.image : slide.image;

const currentSlideData = computed(() => slides[currentSlide.value]);

const { transferBonusAlertsEnabled } = useFeatureFlags();

const features = computed(() => [
    {
        icon: 'dollar-coin',
        text: 'Unlimited credit card benefits tracking',
    },
    { icon: 'bell-ringing', text: 'High-value award alerts' },
    { icon: 'users', text: 'Members-only travel community' },
    { icon: 'card-check', text: 'Best credit cards to use at checkout' },
    { icon: 'email-forward', text: 'Import redemptions from email' },
    ...(transferBonusAlertsEnabled.value
        ? [{ icon: 'transfer-bonuses', text: 'Transfer bonus alerts' }]
        : []),
]);

const prevSlide = () => {
    currentSlide.value =
        (currentSlide.value - 1 + slides.length) % slides.length;
};

const nextSlide = () => {
    currentSlide.value = (currentSlide.value + 1) % slides.length;
};

const closeModal = () => {
    showModal.value = false;
    $cookies.set(
        'travelpluspopup',
        'true',
        '365d',
        '/',
        window.location.hostname,
        true
    );
    appStore.dismissPopup('travel-plus');
    appStore.markSessionPopupShown();
};

onMounted(() => {
    if ($cookies.get('travelpluspopup')) {
        appStore.dismissPopup('travel-plus');
    }
});

watch(
    activePopup,
    (val) => {
        if (val === 'travel-plus') {
            showModal.value = true;
        }
    },
    { immediate: true }
);
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
    transition: opacity 0.2s ease;
}
.slide-fade-enter-from,
.slide-fade-leave-to {
    opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
